import React, { ReactElement, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import Loader from 'src/shared/components/loader/Loader';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { IndividualRouteSetting } from './routesConfig';

type ProtectedRouteProps = {
    redirectPath: string;
    redirectDelay?: number;
    children?: ReactElement;
    isAllowed: boolean;
    routeSetting: IndividualRouteSetting;
};

const ProtectedRoute = (props: ProtectedRouteProps) => {
    const { redirectPath, isAllowed, children, redirectDelay = 5000 } = props;
    const navigate = useAppNavigation();

    // Access is withheld while permissions and the selected site load, so hold the loader for the
    // grace period before giving up. Re-armed whenever access is lost or the destination changes,
    // so a route that mounts allowed and later isn't redirects too, and never to a stale path.
    // `navigate` is deliberately not a dependency: it is rebuilt every render, which would restart
    // the grace period indefinitely.
    useEffect(() => {
        if (isAllowed) {
            return;
        }

        const timer = setTimeout(() => {
            navigate.to(redirectPath, {
                replace: true,
            });
        }, redirectDelay);

        return () => {
            clearTimeout(timer);
        };
    }, [isAllowed, redirectPath, redirectDelay]);

    if (!isAllowed) {
        return <Loader />;
    }

    return children ? children : <Outlet />;
};

export default ProtectedRoute;
